import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { Menu, X, Globe, Building2, ChevronDown, Users, Calendar, Briefcase, Clock, CreditCard, MapPin } from 'lucide-react';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguage();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);

  useEffect(() => {
    const authStatus = localStorage.getItem('authenticated') === 'true';
    setIsAuthenticated(authStatus);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.services-dropdown-container')) {
        setShowServicesDropdown(false);
      }
    };

    if (showServicesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showServicesDropdown]);

  const toggleLanguage = () => {
    changeLanguage(language === 'en' ? 'tr' : 'en');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleServicesDropdown = () => {
    setShowServicesDropdown(!showServicesDropdown);
  };

  const services = [
    {
      id: 'meeting-room',
      icon: Users,
      title: t('home.meetingRooms'),
      description: 'Profesyonel toplantı odaları',
      href: '/arama?type=meeting-room',
      color: 'text-blue-600'
    },
    {
      id: 'subscription',
      icon: CreditCard,
      title: 'Abonelik Planları',
      description: 'Aylık çalışma alanı abonelikleri',
      href: '/abonelik-planlari',
      color: 'text-purple-600'
    },
    {
      id: 'ready-office',
      icon: Building2,
      title: t('home.readyOffices'),
      description: 'Hazır mobilyalı ofisler',
      href: '/arama?type=ready-office',
      color: 'text-green-600'
    },
    {
      id: 'virtual-office',
      icon: Clock,
      title: t('home.virtualOffices'),
      description: 'Sanal ofis hizmetleri',
      href: '/arama?type=virtual-office',
      color: 'text-orange-600'
    },
    {
      id: 'locations',
      icon: MapPin,
      title: 'Tüm Lokasyonlar',
      description: 'Harita üzerinde çalışma alanları',
      href: '/lokasyonlar',
      color: 'text-red-600'
    }
  ];

  // Ana sayfa kontrolü
  const isHomePage = location.pathname === '/';

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 bg-white ${
        isScrolled || !isHomePage
          ? 'shadow-md py-3' 
          : 'py-5'
      }`}
    >
      <div className="container-custom mx-auto flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center group">
          <div className={`flex items-center transition-colors duration-300`}>
            {/* Logo */}
            <div className="h-10 mr-2">
              <img 
                src="/Listcowork-new-logo-siyah-transparan-beyaz copy.png" 
                alt="Listco Work" 
                className="h-full" 
              />
            </div>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-6">
          <Link 
            to="/" 
            className={`font-medium transition-all duration-300 ${
              isScrolled || !isHomePage 
                ? 'text-gray-700 hover:text-primary-600' 
                : 'text-gray-700 hover:text-primary-600 font-semibold'
            }`}
          >
            {t('header.home')}
          </Link>
          
          {/* Services Dropdown */}
          <div className="relative services-dropdown-container">
            <button 
              onClick={toggleServicesDropdown}
              className={`font-medium flex items-center group transition-all duration-300 ${
                isScrolled || !isHomePage 
                  ? 'text-gray-700 hover:text-primary-600' 
                  : 'text-gray-700 hover:text-primary-600'
              }`}
            >
              <span className={`font-bold text-lg ${
                isScrolled || !isHomePage 
                  ? 'bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent' 
                  : 'text-gray-700 bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text'
              }`}>
                Tüm Hizmetler
              </span>
              <ChevronDown size={16} className={`ml-1 transition-transform duration-200 ${
                showServicesDropdown ? 'rotate-180' : ''
              } ${
                isScrolled || !isHomePage 
                  ? 'text-gray-600' 
                  : 'text-gray-700'
              }`} />
            </button>
            
            {/* Dropdown Menu */}
            {showServicesDropdown && (
              <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 py-4 z-50 animate-in slide-in-from-top-2 duration-200">
                <div className="px-4 pb-3 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900">Çalışma Alanı Hizmetleri</h3>
                  <p className="text-sm text-gray-600">Size uygun seçeneği bulun</p>
                </div>
                <div className="py-2">
                  {services.map((service) => {
                    const IconComponent = service.icon;
                    return (
                      <Link
                        key={service.id}
                        to={service.href}
                        className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors group"
                        onClick={() => setShowServicesDropdown(false)}
                      >
                        <div className={`w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center mr-3 group-hover:bg-gray-200 transition-colors`}>
                          <IconComponent size={20} className={service.color} />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 group-hover:text-primary-600 transition-colors">
                            {service.title}
                          </h4>
                          <p className="text-sm text-gray-600">{service.description}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
                <div className="px-4 pt-3 border-t border-gray-100">
                  <Link 
                    to="/arama" 
                    className="block w-full text-center bg-primary-600 text-white py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                    onClick={() => setShowServicesDropdown(false)}
                  >
                    Tümünü Görüntüle
                  </Link>
                </div>
              </div>
            )}
          </div>
          
          <Link 
            to="/is-ortaklari" 
            className={`font-medium transition-all duration-300 ${
              isScrolled || !isHomePage 
                ? 'text-gray-700 hover:text-primary-600' 
                : 'text-gray-700 hover:text-primary-600 font-semibold'
            }`}
          >
            {t('header.forBusiness')}
          </Link>
          
          {/* Contact CTA Button */}
          <a 
            href="tel:+905345177040"
            className={`btn transition-all duration-300 font-medium ${
              isScrolled || !isHomePage
                ? 'btn-primary bg-primary-600 text-white hover:bg-primary-700' 
                : 'bg-primary-600 text-white hover:bg-primary-700 shadow-xl font-semibold border-2 border-primary-600'
            }`}
          >
            Bizi Arayın
          </a>
          
          <button 
            onClick={toggleLanguage} 
            className={`p-2 rounded-full transition-all duration-300 flex items-center font-medium ${
              isScrolled || !isHomePage
                ? 'hover:bg-gray-100 text-gray-700' 
                : 'hover:bg-gray-100 text-gray-700 backdrop-blur-sm bg-white/10 border border-white/20'
            }`}
            aria-label={`${language === 'en' ? 'Türkçe' : 'English'} diline geç`}
          >
            <Globe size={20} />
            <span className="ml-1 font-semibold">{language === 'en' ? 'TR' : 'EN'}</span>
          </button>
        </nav>

        {/* Mobile Menu Button */}
        <div className="flex items-center space-x-2 md:hidden">
          <button 
            onClick={toggleLanguage} 
            className={`p-2 rounded-full transition-colors flex items-center ${
              isScrolled || !isHomePage
                ? 'hover:bg-gray-100 text-gray-700' 
                : 'hover:bg-gray-100 text-gray-700 bg-white/10 backdrop-blur-sm border border-white/20'
            }`}
            aria-label={`${language === 'en' ? 'Türkçe' : 'English'} diline geç`}
          >
            <Globe size={20} />
            <span className="ml-1 text-sm font-medium">{language === 'en' ? 'TR' : 'EN'}</span>
          </button>
          <button 
            onClick={toggleMobileMenu}
            className="p-2 focus:outline-none"
            aria-label="Mobil menüyü aç/kapat"
          >
            {isMobileMenuOpen ? (
              <X size={24} className="text-gray-700" />
            ) : (
              <Menu size={24} className={isScrolled || !isHomePage ? 'text-gray-700' : 'text-gray-700'} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg py-4 px-4 md:hidden border-t">
            <div className="flex flex-col space-y-4">
              <Link to="/" onClick={closeMobileMenu} className="text-gray-700 hover:text-primary-500 py-2 font-medium">
                {t('header.home')}
              </Link>
              
              <Link to="/arama" onClick={closeMobileMenu} className="text-gray-700 hover:text-primary-500 py-2 font-medium">
                Çalışma Alanları
              </Link>
              
              <Link to="/is-ortaklari" onClick={closeMobileMenu} className="text-gray-700 hover:text-primary-500 py-2 font-medium">
                {t('header.forBusiness')}
              </Link>
              
              <a 
                href="tel:+905348266570"
                onClick={closeMobileMenu} 
                className="btn btn-primary w-full"
              >
                Bizi Arayın
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;